import React, {Fragment, FunctionComponent} from "react"
import {useDispatch, useSelector} from "react-redux"
import {actionCreators, State} from '@App/state'
import {useTranslation} from "react-i18next"
import {bindActionCreators} from "@reduxjs/toolkit"
import {ProgressBar} from "@App/Components/common/ProgressBar"
import {Checkbox} from "@App/Components/common/Checkbox";
import {Button} from "@App/Components/Funnel/Button";

export type BrakeScopeProps = {
    headline: string
    step: number
    maxSteps: number
    onSubmit?: (subRepairs: string[]) => void
}

/**
 * Page for choosing the brake scope
 * Custom to the brakes service, therefore living under Pages/BrakesService and not Pages/Common.
 *
 * @param headline
 * @param step
 * @param maxSteps
 * @param onSubmit
 * @constructor
 */
export const BrakeScope: FunctionComponent<BrakeScopeProps> = ({
                                                                   headline,
                                                                   step,
                                                                   maxSteps,
                                                                   onSubmit
                                                               }: BrakeScopeProps) => {
    const {t} = useTranslation()

    // get subRepairs
    const subRepairs = useSelector((state: State) => state.orders.subRepairs)

    const dispatch = useDispatch();
    const {addSubRepair, removeSubRepair} = bindActionCreators(actionCreators, dispatch)

    // add or remove subRepairs from array
    const toggleEquipment = (subRepair: string): void => {
        let exists = subRepairs.some(p => p === subRepair)

        if (exists) {
            removeSubRepair(subRepair)
            return
        }

        if (['brake-pads-front', 'brake-discs-front'].includes(subRepair)) {
            removeSubRepair('brake-pads-front')
            removeSubRepair('brake-discs-front')
            addSubRepair(subRepair)
            return
        }

        if (['brake-pads-back', 'brake-discs-back'].includes(subRepair)) {
            removeSubRepair('brake-pads-back')
            removeSubRepair('brake-discs-back')
            addSubRepair(subRepair)
            return
        }
    }

    const submit = (): void => {
        onSubmit && onSubmit(subRepairs)
    }

    return (
        <Fragment>
            <div className="bg-white pt-5 container">
                <h3 className="text-primary text-3xl font-serif font-weight-normal mb-5">
                    {headline}
                </h3>
                <div className="w-full md:w-1/3">
                    <ProgressBar currentStep={step} steps={maxSteps}/>
                </div>
                <h6 className="text-2xl font-serif mb-3">
                    {t('brakeScope.headline')}
                </h6>
                <div className={'mt-6 pb-6'}>
                    <div className="text-sm text-neutral-700 mb-8">
                        <i>{t('brakeScope.choose-multiple')}</i>
                    </div>
                    <p className={'mb-3'}>{t('brakeScope.front-axle')}</p>
                    <Checkbox
                        id='brake-pads-front'
                        title='Nur Bremsbeläge'
                        status={subRepairs.includes('brake-pads-front')}
                        onChange={() => toggleEquipment('brake-pads-front')}/>
                    <Checkbox
                        id='brake-discs-front'
                        title='Bremsscheiben & Beläge'
                        status={subRepairs.includes('brake-discs-front')}
                        onChange={() => toggleEquipment('brake-discs-front')}/>
                    <p className={'mt-6 mb-3'}>{t('brakeScope.rear-axle')}</p>
                    <Checkbox
                        id='brake-pads-back'
                        title='Nur Bremsbeläge'
                        status={subRepairs.includes('brake-pads-back')}
                        onChange={() => toggleEquipment('brake-pads-back')}/>
                    <Checkbox
                        id='brake-discs-back'
                        title='Bremsscheiben & Beläge'
                        status={subRepairs.includes('brake-discs-back')}
                        onChange={() => toggleEquipment('brake-discs-back')}/>
                    <Button onClick={submit} title={t('brakeScope.submit')}/>
                </div>
            </div>
        </Fragment>
    );
}
